import { useCallback, useEffect, useState } from 'react'
import { patchPermanentRoom, type RoomUpdate } from '@/lib/permanentRooms'

/**
 * Login com Discord e as salas de link fixo.
 *
 * O login serve so pra CRIAR: entrar em sala nenhuma pede conta. Por isso este
 * hook vive na landing e nao no caminho de quem so recebeu um link.
 */

export interface PermanentRoom {
    code: string
    name: string
    requiresPin: boolean
    createdAt: number
    lastSeenAt: number
}

export interface AuthState {
    loading: boolean
    /** O servidor tem login configurado? Sem as chaves do Discord, nao. */
    available: boolean
    user: { id: string; username: string } | null
    rooms: PermanentRoom[]
    /**
     * Esta pessoa tem o cargo que abre o painel do operador?
     *
     * Quem nao tem recebe `false` sem saber que a pergunta foi feita — o
     * servidor so manda o campo pra quem passa. Serve pra mostrar o caminho do
     * painel a quem ja poderia entrar nele: no app instalado nao ha barra de
     * endereco pra digitar `/admin`.
     */
    isAdmin: boolean
    error: string | null
}

const INITIAL: AuthState = {
    loading: true,
    available: false,
    user: null,
    rooms: [],
    isAdmin: false,
    error: null
}

export function usePermanentRooms() {
    const [state, setState] = useState<AuthState>(INITIAL)

    const refresh = useCallback(async () => {
        try {
            const response = await fetch('/api/me')
            if (!response.ok) {
                setState({ loading: false, available: false, user: null, rooms: [], isAdmin: false, error: null })
                return
            }
            const data = await response.json() as {
                loggedIn?: boolean
                loginAvailable?: boolean
                isAdmin?: boolean
                user?: { id: string; username: string }
                rooms?: PermanentRoom[]
            }
            setState({
                loading: false,
                available: !!data.loginAvailable,
                user: data.loggedIn ? data.user ?? null : null,
                rooms: data.rooms ?? [],
                isAdmin: !!data.loggedIn && !!data.isAdmin,
                error: null
            })
        } catch {
            setState({ loading: false, available: false, user: null, rooms: [], isAdmin: false, error: null })
        }
    }, [])

    useEffect(() => {
        void refresh()
    }, [refresh])

    const createRoom = useCallback(async (name: string, pin: string) => {
        setState((current) => ({ ...current, error: null }))
        try {
            const response = await fetch('/api/rooms', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ name, pin: pin || undefined })
            })
            const data = await response.json() as { code?: string; error?: string; limit?: number }

            if (!response.ok) {
                setState((current) => ({ ...current, error: messageFor(data.error, data.limit) }))
                return null
            }

            await refresh()
            return data.code ?? null
        } catch {
            setState((current) => ({ ...current, error: 'Nao consegui criar a sala. Tente de novo.' }))
            return null
        }
    }, [refresh])

    /**
     * Edita uma sala fixa do proprio dono.
     *
     * `pin` tem tres significados e a distincao e o ponto todo: `undefined`
     * mantem o PIN atual, string vazia remove, e uma string troca. Sem isso,
     * salvar so o nome derrubaria a tranca sem querer.
     */
    const updateRoom = useCallback(async (code: string, fields: RoomUpdate) => {
        setState((current) => ({ ...current, error: null }))
        const resultado = await patchPermanentRoom(code, fields)
        if (!resultado.ok) {
            setState((current) => ({ ...current, error: messageFor(resultado.error) }))
            return false
        }
        await refresh()
        return true
    }, [refresh])

    const deleteRoom = useCallback(async (code: string) => {
        try {
            await fetch(`/api/rooms/${encodeURIComponent(code)}`, { method: 'DELETE' })
            await refresh()
        } catch {
            setState((current) => ({ ...current, error: 'Nao consegui apagar a sala.' }))
        }
    }, [refresh])

    return {
        ...state,
        createRoom,
        updateRoom,
        deleteRoom,
        refresh,
        clearError: () => setState((current) => ({ ...current, error: null }))
    }
}

function messageFor(error: string | undefined, limit?: number): string {
    switch (error) {
        case 'login_required': return 'Faca login com o Discord pra criar uma sala fixa.'
        case 'invalid_name': return 'Escolha um nome pra sala.'
        case 'invalid_pin': return 'O PIN precisa ter de 4 a 12 caracteres.'
        case 'too_many_rooms': return `Voce ja tem ${limit ?? 'muitas'} salas fixas. Apague uma pra criar outra.`
        case 'not_found': return 'Essa sala nao existe mais (ou nao e sua).'
        case 'code_taken': return 'Ja existe uma sala nesse endereco. Escolha outro nome.'
        case 'invalid_slug': return 'Esse nome nao vira um endereco valido. Use letras ou numeros.'
        case 'room_in_use': return 'Tem gente na sala agora — o endereco so muda com ela vazia.'
        case 'code_must_match_name': return 'O endereco tem que ser o do nome da sala.'
        case 'network': return 'Nao consegui falar com o servidor.'
        default: return 'Nao consegui criar a sala. Tente de novo.'
    }
}
